/*
 * STRATOS motion layer
 *
 * Fades and lifts content as it scrolls into view. Selectors are deliberately
 * generic theme classes, so sections added later in the theme editor pick the
 * behaviour up without further wiring.
 *
 * Reveal state is driven by a throttled scroll/resize pass rather than an
 * IntersectionObserver: the observer left elements stranded at opacity 0 when
 * several scrolled past within one callback, and content that never reappears
 * is a far worse outcome than a missing animation.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  root.classList.add('js-motion');

  /* Groups whose direct children stagger; everything else reveals on its own. */
  var STAGGER_GROUPS = [
    '.rich-text__blocks',
    '.image-with-text__content',
    '.flex-grid',
    '.accordion-wrapper',
    '.collapsible-content__wrapper',
  ];

  var SINGLES = [
    '.title-wrapper',
    '.section--padding > .page-width > .rte',
    '.contact__fields',
    '.newsletter__wrapper',
  ];

  var STAGGER_STEP = 70;
  var STAGGER_MAX = 420;
  var MARGIN = 80; // reveal slightly before the element's top edge is reached

  var targets = [];
  var ticking = false;

  function markReveal(el, index) {
    if (!el || el.hasAttribute('data-stratos-reveal')) return false;
    el.setAttribute('data-stratos-reveal', '');
    if (index) {
      el.style.setProperty('--stratos-reveal-delay', Math.min(index * STAGGER_STEP, STAGGER_MAX) + 'ms');
    }
    return true;
  }

  function collect(scope) {
    var found = [];

    STAGGER_GROUPS.forEach(function (selector) {
      Array.prototype.forEach.call(scope.querySelectorAll(selector), function (group) {
        Array.prototype.forEach.call(group.children, function (child, i) {
          if (markReveal(child, i)) found.push(child);
        });
      });
    });

    SINGLES.forEach(function (selector) {
      Array.prototype.forEach.call(scope.querySelectorAll(selector), function (el) {
        if (markReveal(el, 0)) found.push(el);
      });
    });

    return found;
  }

  function reveal(el) {
    el.setAttribute('data-stratos-revealed', '');
  }

  function update() {
    ticking = false;
    var limit = window.innerHeight + MARGIN;
    var remaining = [];

    for (var i = 0; i < targets.length; i++) {
      var el = targets[i];
      if (el.getBoundingClientRect().top < limit) reveal(el);
      else remaining.push(el);
    }

    targets = remaining;

    if (!targets.length) {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    }
  }

  function schedule() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  /* Primary trigger. An observer catches scrolling inside any container, which
     a window scroll listener alone does not. */
  var observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              reveal(entry.target);
              observer.unobserve(entry.target);
            });
          },
          {rootMargin: '0px 0px -5% 0px', threshold: 0}
        )
      : null;

  function observe(scope) {
    var found = collect(scope || document);
    if (!found.length) return;

    if (!observer || !window.requestAnimationFrame) {
      found.forEach(reveal);
      return;
    }

    targets = targets.concat(found);
    found.forEach(function (el) {
      observer.observe(el);
    });

    window.addEventListener('scroll', schedule, {passive: true});
    window.addEventListener('resize', schedule);
    update();

    /* Failsafe. Whatever happens to the observer, the scroll listener or the
       layout, nothing stays invisible: after this window everything is shown. */
    window.setTimeout(function () {
      found.forEach(reveal);
    }, 5000);
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    observe(document);
    // Late-loading fonts and images shift the layout; re-measure once settled.
    window.addEventListener('load', schedule);
  });

  /* Sections re-render as the merchant edits them in the theme editor. */
  document.addEventListener('shopify:section:load', function (event) {
    observe(event.target);
  });
})();
